import { Button, DatePicker, Input, Modal } from "antd";
import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import Loader from "../../../pages/pages/loader";
import CustomersDropdown from "../../CustomersDropdown";
import PlatformsDropdown from "../../PlatformsDropdown";
import VideoDurationDropdown from "../../VideoDurationDropdown";
import {
  useCreateCustomerCampaignPlatformMutation,
  useUpdateCustomerCampaignPlatformMutation,
} from "../../../redux/Api/CampaignManagment/cMCustomerCampaignPlatformApiSlice";
import { useListVideosDurationQuery } from "../../../redux/Api/VideoApiSlice";

const AddCustomerPlatformModal = ({
  open,
  setOpen,
  campaignId,
  holder = null,
  refetch,
}) => {
  const isEdit = holder && holder.id;
  const isDuplicate = holder && !isEdit;
  const [create, { isLoading: isCreating }] =
    useCreateCustomerCampaignPlatformMutation();
  const [update, { isLoading: isUpdating }] =
    useUpdateCustomerCampaignPlatformMutation();

  // Handle both object {id} and direct number formats
  const getCustomerId = (holder) => {
    if (!holder) return null;
    if (typeof holder.customer === "number") return holder.customer;
    if (holder.customer?.id) return holder.customer.id;
    return null;
  };

  const getPlatformId = (holder) => {
    if (!holder) return null;
    if (typeof holder.platform === "number") return holder.platform;
    if (holder.platform?.id) return holder.platform.id;
    return null;
  };

  const getTakeDownDurationId = (holder, durations = []) => {
    if (!holder) return null;
    const td = holder.take_down_duration;
    if (td == null) return null;
    if (typeof td === "number") return td;
    if (typeof td === "object" && td?.id != null) return td.id;
    if (typeof td === "string") {
      const match = durations.find((d) => d.duration === td);
      return match?.id ?? null;
    }
    return null;
  };

 const { data: videosDuration } = useListVideosDurationQuery({
    page: "*",
    ordering: "id",
  }); 

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      campaign: campaignId,
      customer: getCustomerId(holder),
      platform: isEdit
        ? getPlatformId(holder)
        : getPlatformId(holder)
          ? [getPlatformId(holder)]
          : [],
      start_date: holder?.start_date || null,
      end_date: holder?.end_date || null,
      is_active: holder?.is_active !== false,
      campaign_rights_owner: holder?.campaign_rights_owner || "",
      ownership_territory: holder?.ownership_territory || "",
      policy: holder?.policy || "",
      take_down_duration: getTakeDownDurationId(
        holder,
        videosDuration?.results,
      ),
      campaign_loa_dropbox_url: holder?.campaign_loa_dropbox_url || "",
    },
    validationSchema: Yup.object({
      customer: Yup.number().required("Please select a customer"),
      platform: isEdit
        ? Yup.number().required("Please select a platform")
        : Yup.array()
            .of(Yup.number())
            .min(1, "Please select at least one platform")
            .required("Please select at least one platform"),
      start_date: Yup.string().required("Please select start date"),
      end_date: Yup.string().required("Please select end date"),
      is_active: Yup.boolean(),
      campaign_rights_owner: Yup.string().required("Please enter rights owner"),
      ownership_territory: Yup.string().required("Please enter territory"),
      policy: Yup.string().required("Please enter policy"),
      take_down_duration: Yup.number().required(
        "Please select take down duration",
      ),
    }),
    onSubmit: async (values) => {
      try {
        if (isEdit) {
          await update({ id: holder.id, data: values }).unwrap();
          toast.success("Assignment updated successfully");
        } else {
          const { platform, ...rest } = values;
          const platformIds = Array.isArray(platform) ? platform : [platform];
          const payload =
            platformIds.length === 1
              ? { ...rest, platform: platformIds[0] }
              : platformIds.map((pid) => ({ ...rest, platform: pid }));

          const result = await create(payload).unwrap();
          const count = Array.isArray(result) ? result.length : platformIds.length;
          toast.success(`Added ${count} assignment(s)`);
        }
        refetch?.();
        setOpen(false);
      } catch (error) {
        if (error?.data) {
          const errorMessages = Object.values(error.data).flat();
          errorMessages.forEach((message) => {
            toast.error(message);
          });
        } else {
          console.error("Error:", error);
          toast.error("Failed to save assignment");
        }
      }
    },
  });

  const handleSubmit = async () => {
    const errors = await validation.validateForm();
    console.log(validation.values);
    if (Object.keys(errors).length !== 0) {
      Object.keys(errors).forEach((key) => {
        toast.error(errors[key]);
      });
      return;
    }
    validation.handleSubmit();
  };

  return (
    <div className="w-full">
      <Loader isLoading={isCreating || isUpdating} />
      <Modal
        open={open}
        title={
          isEdit
            ? "Edit Assignment"
            : isDuplicate
              ? "Duplicate Entry"
              : "Add Customer-Platform Entry"
        }
        onCancel={() => setOpen(false)}
        footer={[
          <Button key="back" onClick={() => setOpen(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleSubmit}>
            {isEdit ? "Update" : "Add"}
          </Button>,
        ]}
      >
        <div className="flex flex-col gap-4">
          {/* Row 1: Customer & Platform */}
          <div className="flex gap-8">
            <div className="flex-1">
              <CustomersDropdown
                onBlur={validation.handleBlur}
                onChange={(val) => validation.setFieldValue("customer", val)}
                value={validation.values?.customer}
              />
              {validation.touched.customer && validation.errors.customer ? (
                <p className="text-danger">{validation.errors.customer}</p>
              ) : null}
            </div>
            <div className="flex-1">
              <PlatformsDropdown
                onBlur={validation.handleBlur}
                onChange={(val) => validation.setFieldValue("platform", val)}
                value={validation.values?.platform}
                multi={!isEdit}
              />
              {validation.touched.platform && validation.errors.platform ? (
                <p className="text-danger">{validation.errors.platform}</p>
              ) : null}
            </div>
          </div>

          {/* Row 2: Start & End Dates */}
          <div className="flex gap-8">
            <div className="flex-1">
              <label className="form-label">Start Date</label>
              <DatePicker
                style={{ width: "100%" }}
                name="start_date"
                value={
                  validation.values?.start_date
                    ? dayjs(validation.values.start_date)
                    : null
                }
                onChange={(e, date) =>
                  validation.setFieldValue("start_date", date)
                }
                onBlur={validation.handleBlur}
              />
              {validation.touched.start_date && validation.errors.start_date ? (
                <p className="text-danger">{validation.errors.start_date}</p>
              ) : null}
            </div>
            <div className="flex-1">
              <label className="form-label">End Date</label>
              <DatePicker
                style={{ width: "100%" }}
                name="end_date"
                value={
                  validation.values?.end_date
                    ? dayjs(validation.values.end_date)
                    : null
                }
                onChange={(e, date) =>
                  validation.setFieldValue("end_date", date)
                }
                onBlur={validation.handleBlur}
              />
              {validation.touched.end_date && validation.errors.end_date ? (
                <p className="text-danger">{validation.errors.end_date}</p>
              ) : null}
            </div>
          </div>

          {/* Row 4: Rights Owner & Territory */}
          <div className="flex gap-8">
            <div className="flex-1">
              <label className="form-label">Rights Owner</label>
              <Input
                name="campaign_rights_owner"
                value={validation.values?.campaign_rights_owner}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
              />
              {validation.touched.campaign_rights_owner &&
              validation.errors.campaign_rights_owner ? (
                <p className="text-danger">
                  {validation.errors.campaign_rights_owner}
                </p>
              ) : null}
            </div>
            <div className="flex-1">
              <label className="form-label">Territory</label>
              <Input
                name="ownership_territory"
                value={validation.values?.ownership_territory}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
              />
              {validation.touched.ownership_territory &&
              validation.errors.ownership_territory ? (
                <p className="text-danger">
                  {validation.errors.ownership_territory}
                </p>
              ) : null}
            </div>
          </div>

          {/* Row 5: Policy */}
          <div className="flex gap-8">
            <div className="flex-1">
              <label className="form-label">Policy</label>
              <Input
                name="policy"
                value={validation.values?.policy}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
              />
              {validation.touched.policy && validation.errors.policy ? (
                <p className="text-danger">{validation.errors.policy}</p>
              ) : null}
            </div>
          </div>

          {/* Row 6: Take Down Duration & LOA URL */}
          <div className="flex gap-8">
            <div className="flex-1">
              <VideoDurationDropdown
                onChange={(val) =>
                  validation.setFieldValue("take_down_duration", val)
                }
                value={validation.values?.take_down_duration}
                title="Take Down Duration"
              />
              {validation.touched.take_down_duration &&
              validation.errors.take_down_duration ? (
                <p className="text-danger">
                  {validation.errors.take_down_duration}
                </p>
              ) : null}
            </div>
            <div className="flex-1">
              <label className="form-label">LOA URL</label>
              <Input
                name="campaign_loa_dropbox_url"
                value={validation.values?.campaign_loa_dropbox_url}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AddCustomerPlatformModal;
